// src/utils/staffSettingsUtils.js
//
// Single source of truth for per-staff settings that affect behavior across the
// whole app: team messaging visibility, dropdown visibility, and the three
// email opt-ins.
//
// Every page that reads these flags should import from here instead of reading
// the raw Firestore fields, so the semantics (and especially the DEFAULTS for
// staff records that predate a flag) stay identical everywhere.
//
// STORAGE SHAPE on users/{uid}:
//   hideFromTeamMessaging : boolean   (true  = hidden from Team Messaging)
//   hideFromScheduling    : boolean   (true  = hidden from staff dropdowns)
//   notificationPrefs: {
//     vehicles    : boolean           (Vehicle Expiration reminder emails)
//     jobSchedule : boolean           (New Job Scheduled emails)
//     jobUpdates  : boolean           (Job note / update emails)
//   }
//
// NOTE: the Cloud Functions codebase (defaultcodebase/index.js) intentionally
// duplicates the resolver logic below, because functions cannot import from
// src/. If you change a DEFAULT here, change it there too.

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------
//
// Defaults are chosen to PRESERVE existing behavior for staff records that were
// created before a given flag existed:
//
//   jobSchedule / jobUpdates -> true
//       Today every assigned tech with an email gets these. Defaulting to false
//       would silently switch off job emails for the entire existing roster.
//
//   vehicles -> false
//       Vehicle reminders currently go to the notificationRecipients/vehicles
//       list. Staff opt-ins are ADDITIVE on top of that list, so defaulting to
//       true would suddenly blast the whole roster.
//
//   hideFromTeamMessaging / hideFromScheduling -> false (visible)
//       Matches how the existing filters already treat a missing field.

export const STAFF_SETTINGS_DEFAULTS = Object.freeze({
  hideFromTeamMessage: false,
  receiveVehicleExpirationEmails: false,
  receiveJobScheduleEmails: true,
  receiveJobUpdateEmails: true,
  showInDropdowns: true,
});

const toBool = (value, fallback) =>
  value === true ? true : value === false ? false : fallback;

// ---------------------------------------------------------------------------
// Column / field definitions (drives the StaffSettings grid and the Staff modal)
// ---------------------------------------------------------------------------

export const STAFF_SETTING_FIELDS = Object.freeze([
  {
    key: "hideFromTeamMessage",
    label: "Hide From Team Message",
    shortLabel: "Hide Team Msg",
    yesMeans: "Hidden from Team Messaging",
    noMeans: "Shown in Team Messaging",
    help: "Yes removes this person from the Team Messaging recipient list.",
  },
  {
    key: "receiveVehicleExpirationEmails",
    label: "Receive Vehicle Expiration Emails",
    shortLabel: "Vehicle Exp.",
    yesMeans: "Receives vehicle expiration reminders",
    noMeans: "Does not receive vehicle expiration reminders",
    help:
      "Yes adds this person to the daily vehicle inspection / registration / service reminder emails, in addition to the Notifications recipient list.",
  },
  {
    key: "receiveJobScheduleEmails",
    label: "Receive Job Schedule Emails",
    shortLabel: "Job Schedule",
    yesMeans: "Receives New Job Scheduled emails",
    noMeans: "Does not receive New Job Scheduled emails",
    help:
      "Yes emails this person when they are assigned to a newly scheduled job.",
  },
  {
    key: "receiveJobUpdateEmails",
    label: "Receive Job Update Emails",
    shortLabel: "Job Updates",
    yesMeans: "Receives job note / update emails",
    noMeans: "Does not receive job note / update emails",
    help:
      "Yes emails this person when a note is added to a job they are assigned to.",
  },
  {
    key: "showInDropdowns",
    label: "Show In Dropdowns",
    shortLabel: "Dropdowns",
    yesMeans: "Appears in staff dropdowns",
    noMeans: "Hidden from staff dropdowns",
    help:
      "No hides this person from Tech Assign dropdowns and scheduling suggestions across the app. Existing assignments are not changed.",
  },
]);

export const STAFF_SETTING_KEYS = STAFF_SETTING_FIELDS.map((f) => f.key);

// ---------------------------------------------------------------------------
// Read: user doc -> normalized settings object
// ---------------------------------------------------------------------------

export const resolveStaffSettings = (user = {}) => {
  const prefs = user?.notificationPrefs || {};

  return {
    hideFromTeamMessage: toBool(
      user?.hideFromTeamMessaging,
      STAFF_SETTINGS_DEFAULTS.hideFromTeamMessage
    ),
    receiveVehicleExpirationEmails: toBool(
      prefs?.vehicles,
      STAFF_SETTINGS_DEFAULTS.receiveVehicleExpirationEmails
    ),
    receiveJobScheduleEmails: toBool(
      prefs?.jobSchedule,
      STAFF_SETTINGS_DEFAULTS.receiveJobScheduleEmails
    ),
    receiveJobUpdateEmails: toBool(
      prefs?.jobUpdates,
      STAFF_SETTINGS_DEFAULTS.receiveJobUpdateEmails
    ),
    // stored inverted: hideFromScheduling === true means hidden
    showInDropdowns: !toBool(
      user?.hideFromScheduling,
      !STAFF_SETTINGS_DEFAULTS.showInDropdowns
    ),
  };
};

// True when the user doc has NO explicit value stored for a given setting yet.
export const isStaffSettingUnset = (user = {}, key = "") => {
  const prefs = user?.notificationPrefs || {};

  switch (key) {
    case "hideFromTeamMessage":
      return typeof user?.hideFromTeamMessaging !== "boolean";
    case "receiveVehicleExpirationEmails":
      return typeof prefs?.vehicles !== "boolean";
    case "receiveJobScheduleEmails":
      return typeof prefs?.jobSchedule !== "boolean";
    case "receiveJobUpdateEmails":
      return typeof prefs?.jobUpdates !== "boolean";
    case "showInDropdowns":
      return typeof user?.hideFromScheduling !== "boolean";
    default:
      return false;
  }
};

// ---------------------------------------------------------------------------
// Write: normalized settings object -> Firestore update payload
// ---------------------------------------------------------------------------
//
// Uses dotted paths for notificationPrefs so updateDoc MERGES into the existing
// map instead of replacing it. Other prefs already on the record (jobs,
// proposals, etc.) are left untouched.

export const buildStaffSettingsPatch = (settings = {}) => {
  const resolved = { ...STAFF_SETTINGS_DEFAULTS, ...settings };

  return {
    hideFromTeamMessaging: resolved.hideFromTeamMessage === true,
    hideFromScheduling: resolved.showInDropdowns !== true,
    "notificationPrefs.vehicles":
      resolved.receiveVehicleExpirationEmails === true,
    "notificationPrefs.jobSchedule": resolved.receiveJobScheduleEmails === true,
    "notificationPrefs.jobUpdates": resolved.receiveJobUpdateEmails === true,
  };
};

// Same values, but shaped as a nested object. Use this when merging into a
// local in-memory record (e.g. Staff.js editedStaff) rather than writing to
// Firestore with dotted paths.
export const applyStaffSettingsToRecord = (record = {}, settings = {}) => {
  const resolved = { ...resolveStaffSettings(record), ...settings };

  return {
    ...record,
    hideFromTeamMessaging: resolved.hideFromTeamMessage === true,
    hideFromScheduling: resolved.showInDropdowns !== true,
    notificationPrefs: {
      ...(record?.notificationPrefs || {}),
      vehicles: resolved.receiveVehicleExpirationEmails === true,
      jobSchedule: resolved.receiveJobScheduleEmails === true,
      jobUpdates: resolved.receiveJobUpdateEmails === true,
    },
  };
};

// ---------------------------------------------------------------------------
// Convenience predicates for consumer pages
// ---------------------------------------------------------------------------

export const isStaffDeactivated = (user = {}) => {
  const status = String(user?.status || "").toLowerCase();
  return (
    user?.isActive === false ||
    status === "deactivated" ||
    status === "inactive" ||
    status === "terminated"
  );
};

export const showInTeamMessaging = (user = {}) =>
  resolveStaffSettings(user).hideFromTeamMessage !== true;

export const showInDropdowns = (user = {}) =>
  resolveStaffSettings(user).showInDropdowns === true;

export const receivesVehicleExpirationEmails = (user = {}) =>
  resolveStaffSettings(user).receiveVehicleExpirationEmails === true;

export const receivesJobScheduleEmails = (user = {}) =>
  resolveStaffSettings(user).receiveJobScheduleEmails === true;

export const receivesJobUpdateEmails = (user = {}) =>
  resolveStaffSettings(user).receiveJobUpdateEmails === true;

export const getStaffDisplayName = (user = {}) =>
  `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
  user?.name ||
  user?.email ||
  "";

// Canonical dropdown roster: active staff who are not hidden from dropdowns,
// sorted by display name. Use this anywhere a staff <select> is built.
export const filterStaffForDropdowns = (users = []) =>
  (Array.isArray(users) ? users : [])
    .filter((user) => !isStaffDeactivated(user))
    .filter((user) => showInDropdowns(user))
    .filter((user) => getStaffDisplayName(user))
    .sort((a, b) =>
      getStaffDisplayName(a).localeCompare(getStaffDisplayName(b), undefined, {
        sensitivity: "base",
      })
    );

export const WORKER_TYPE_OPTIONS = Object.freeze([
  "Employee",
  "Sub Contractor",
]);

export const STAFF_STATUS_OPTIONS = Object.freeze([
  "Active",
  "Deactivated",
]);
